import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import React, { useContext, useState } from 'react';
import { translate } from '../../../../../types/translation/Translator';
import moment from 'moment';
import { ReorderRuleExportConfigurationContext } from '../../../../../context/ReorderRuleExportConfigurationContext';
import { ExportItems } from '../../../../components/ExportItems';
import { Routing } from '../../../../../util/routes.util';
import { ReorderRuleExportConfigurationRequest } from '../../../../../requests/reorderRuleExportConfiguration.request';
import { useRequest } from '../../../../../requests/common/requestHook';
import { ActionModal } from '../../../../components/Modal';
import { Button, CancelButton } from '../../../../components/Button';
import { ExportUtil } from '../../../../../util/export.util';
import { ExportFile } from '../../../../../types/export/export';

interface ExportReorderRuleModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  groupIds: Set<string>;
}

export function ExportReorderRuleModal({ open, setOpen, groupIds }: ExportReorderRuleModalProps) {
  const { reorderRuleExportConfigurations } = useContext(ReorderRuleExportConfigurationContext);

  const [exportReorderRules, { loading }] = useRequest(ReorderRuleExportConfigurationRequest.download);

  const [selected, setSelected] = useState<Set<string>>(
    new Set([...reorderRuleExportConfigurations.values()].filter(config => config.default).map(config => config.id)),
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = async () => {
    if (!selected.size) {
      const file = await exportReorderRules({
        timezone: moment.tz.guess(),
        locale: (window.navigator as any)['userLanguage'] || window.navigator.language,
        groupIds: [...groupIds.values()],
      });
      if (!file) return;
      ExportUtil.downloadExport(file);
    } else {
      const files: ExportFile[] = [];
      for (const configId of Array.from(selected)) {
        const file = await exportReorderRules({
          configId,
          timezone: moment.tz.guess(),
          locale: (window.navigator as any)['userLanguage'] || window.navigator.language,
          groupIds: [...groupIds.values()],
        });
        if (file) files.push(file);
      }
      if (!files.length) return;

      if (files.length === 1) {
        ExportUtil.downloadExport(files[0]);
      } else {
        ExportUtil.downloadZip(files, reorderRuleExportConfigurations);
      }
    }

    setSelected(new Set());
    handleClose();
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={handleClose} disabled={loading} />
      <Button loading={loading} text={translate({ key: TranslationKey.export })} onClick={handleDownload} />
    </div>
  );

  return (
    <ActionModal
      footer={footer}
      title={translate({ key: TranslationKey.exportReorderRules })}
      open={open}
      setOpen={setOpen}
      className='max-w-[720px]'
    >
      <ExportItems
        items={[...reorderRuleExportConfigurations.values()]}
        selected={selected}
        setSelected={setSelected}
        createPath={Routing.productMasterData.settings.stockTriggers.settings.report.create()}
      />
    </ActionModal>
  );
}
